/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/ComplaintDashboardTiles.tsx
 * PURPOSE: Dashboard tiles showing complaint statistics with SLA tracking
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-03-31
 * COMPLIANCE: SDLC 6.3.1 + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Complaint Management
 * - Impact: Operational — SLA tracking and complaint resolution monitoring
 * - Stakeholders: Branch Managers, HR Team, Tenant Admins
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Data Visualization
 * - Pattern: React Component, Auto-refresh polling, Reusable MetricTile
 * - Dependencies: React 18, TypeScript, lucide-react, apiV2
 *
 * @cross-references
 * - Pattern: frontend/src/components/TaskDashboardTiles.tsx (pattern source)
 * - API: backend/app/api/v2/complaints.py (GET /api/v2/complaints/summary)
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquareWarning, Clock, AlertTriangle,
  CheckCircle, XCircle, Timer
} from 'lucide-react';
import { apiV2 } from '../services/api/config';

interface ComplaintSummary {
  total: number;
  by_status: {
    SUBMITTED: number;
    UNDER_REVIEW: number;
    APPROVED: number;
    REJECTED: number;
    ESCALATED: number;
  };
  sla_breach_count: number;
  avg_resolution_hours: number | null;
  period: {
    start: string;
    end: string;
  };
}

interface ComplaintDashboardTilesProps {
  refreshInterval?: number; // In milliseconds, default 60000 (1 minute)
  onTileClick?: (metric: string) => void;
}

export const ComplaintDashboardTiles: React.FC<ComplaintDashboardTilesProps> = ({
  refreshInterval = 60000,
  onTileClick
}) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<ComplaintSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Load complaint summary
  const loadSummary = async () => {
    try {
      const response = await apiV2.get('/complaints/summary');
      setSummary(response.data);
      setLastUpdated(new Date());
      setError('');
    } catch (err: any) {
      setError(t('errors.load_summary_failed', 'Failed to load summary'));
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh
  useEffect(() => {
    loadSummary();
    const interval = setInterval(loadSummary, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  // Derived metrics
  const pendingCount = summary
    ? (summary.by_status.SUBMITTED + summary.by_status.UNDER_REVIEW)
    : 0;
  const avgTimeDisplay = summary?.avg_resolution_hours != null
    ? `${summary.avg_resolution_hours.toFixed(1)}h`
    : 'N/A';

  // Tile component
  const MetricTile: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string | number;
    subValue?: string;
    color: string;
    onClick?: () => void;
  }> = ({ icon, label, value, subValue, color, onClick }) => {
    return (
      <div
        onClick={onClick}
        className={`bg-white rounded-lg shadow-sm p-6 border-l-4 ${color} ${
          onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{label}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {subValue && (
              <p className="text-xs text-gray-500 mt-1">{subValue}</p>
            )}
          </div>
          <div className={`p-3 rounded-full ${color.replace('border-l-', 'bg-').replace('500', '100')}`}>
            {icon}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Complaint metrics — 6 tiles in 3-column grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricTile
          icon={<MessageSquareWarning className="text-blue-500" size={24} />}
          label={t('complaint.total', 'Total Complaints')}
          value={summary?.total || 0}
          subValue={summary?.period
            ? `${summary.period.start} — ${summary.period.end}`
            : undefined}
          color="border-l-blue-500"
          onClick={() => onTileClick?.('total')}
        />

        <MetricTile
          icon={<Clock className="text-orange-500" size={24} />}
          label={t('complaint.pending', 'Đang chờ xử lý')}
          value={pendingCount}
          subValue={`${summary?.by_status.SUBMITTED || 0} ${t('complaint.submitted', 'submitted')} + ${summary?.by_status.UNDER_REVIEW || 0} ${t('complaint.under_review', 'under review')}`}
          color="border-l-orange-500"
          onClick={() => onTileClick?.('pending')}
        />

        <MetricTile
          icon={<AlertTriangle className="text-red-500" size={24} />}
          label={t('complaint.sla_breached', 'SLA Breached')}
          value={summary?.sla_breach_count || 0}
          subValue={t('complaint.requires_attention', 'Requires immediate attention')}
          color="border-l-red-500"
          onClick={() => onTileClick?.('sla_breached')}
        />

        <MetricTile
          icon={<CheckCircle className="text-green-500" size={24} />}
          label={t('complaint.approved', 'Đã giải quyết')}
          value={summary?.by_status.APPROVED || 0}
          subValue={t('complaint.resolved', 'Resolved')}
          color="border-l-green-500"
          onClick={() => onTileClick?.('approved')}
        />

        <MetricTile
          icon={<XCircle className="text-gray-500" size={24} />}
          label={t('complaint.rejected', 'Từ chối')}
          value={summary?.by_status.REJECTED || 0}
          subValue={t('complaint.rejected_sub', 'Rejected')}
          color="border-l-gray-500"
          onClick={() => onTileClick?.('rejected')}
        />

        <MetricTile
          icon={<Timer className="text-teal-500" size={24} />}
          label={t('complaint.avg_resolution_time', 'Thời gian giải quyết TB')}
          value={avgTimeDisplay}
          subValue={t('complaint.per_complaint', 'Per complaint')}
          color="border-l-teal-500"
        />
      </div>

      {/* Last updated indicator */}
      <div className="text-xs text-gray-500 text-right">
        {t('common.last_updated', 'Last updated')}: {lastUpdated.toLocaleTimeString()}
      </div>
    </div>
  );
};

// Export a compact version for smaller displays
export const ComplaintDashboardCompact: React.FC<{ onExpand?: () => void }> = ({ onExpand }) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<ComplaintSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const response = await apiV2.get('/complaints/summary');
        setSummary(response.data);
      } catch (err) {
        // Silent fail for compact widget
      } finally {
        setLoading(false);
      }
    };
    loadSummary();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i}>
              <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const pendingCount = summary
    ? (summary.by_status.SUBMITTED + summary.by_status.UNDER_REVIEW)
    : 0;

  return (
    <div
      className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
      onClick={onExpand}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-900">{t('complaint.quick_stats', 'Complaint Quick Stats')}</h3>
        <span className="text-xs text-gray-500">{t('common.click_to_expand', 'Click to expand')}</span>
      </div>

      <div className="grid grid-cols-4 gap-4 text-center">
        <div>
          <p className="text-2xl font-bold text-blue-600">{summary?.total || 0}</p>
          <p className="text-xs text-gray-600">{t('complaint.total_short', 'Total')}</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-orange-600">{pendingCount}</p>
          <p className="text-xs text-gray-600">{t('complaint.pending_short', 'Pending')}</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-red-600">{summary?.sla_breach_count || 0}</p>
          <p className="text-xs text-gray-600">{t('complaint.sla_short', 'SLA Breach')}</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-green-600">{summary?.by_status.APPROVED || 0}</p>
          <p className="text-xs text-gray-600">{t('complaint.resolved_short', 'Resolved')}</p>
        </div>
      </div>
    </div>
  );
};
